/**
 * PepBox Personalisation
 *
 * Modules:
 *  1. Utils          — UUID, money formatting, cart API wrappers
 *  2. UI             — panel show/hide, price breakdown, submit guard
 *  3. Upload         — Cloudinary widget per section
 *  4. CartManager    — capture-phase form intercept, Cart API calls
 *  5. SyncManager    — quantity sync + orphan cleanup via fetch interceptor
 *  6. Boot           — initialise all sections on DOMContentLoaded
 */

/* ─────────────────────────────────────────────────────────────
   1. UTILS
───────────────────────────────────────────────────────────── */

function uuidV4() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

// Fills the shop's own money_format template (shop.money_format — same symbol and
// placement Shopify already renders everywhere else) and drops ".00" when the
// amount is a whole number. Doesn't decide currency/symbol itself at all.
function formatMoney(cents, moneyFormat) {
  const amount = cents / 100;
  const hasCents = Math.round(amount * 100) % 100 !== 0;
  const formattedAmount = amount.toLocaleString('en-US', {
    minimumFractionDigits: hasCents ? 2 : 0,
    maximumFractionDigits: 2,
  });
  return (moneyFormat || '${{amount}}').replace(/\{\{\s*amount\w*\s*\}\}/, formattedAmount);
}

async function cartFetch(endpoint, body) {
  const res = await fetch(`/cart/${endpoint}.js`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`Cart ${endpoint} failed: ${res.status}`);
  return res.json();
}

async function getCart() {
  const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
  if (!res.ok) throw new Error('Failed to fetch cart');
  return res.json();
}

// Section keys come from templates/cart.json IDs (not HTML element IDs or section filenames).
// Read them dynamically from dataset.id — mirrors cart.js getSectionsToRender() lines 122-145.
function renderCartSections(sections, cartItemsSectionId) {
  if (!sections) return;
  const config = [
    { key: cartItemsSectionId, elId: 'main-cart-items', selector: '.js-contents' },
    { key: 'cart-icon-bubble', elId: 'cart-icon-bubble', selector: '.shopify-section' },
    { key: 'cart-live-region-text', elId: 'cart-live-region-text', selector: '.shopify-section' },
  ];
  const parser = new DOMParser();
  for (const { key, elId, selector } of config) {
    if (!key || !sections[key]) continue;
    const newDoc = parser.parseFromString(sections[key], 'text/html');
    const src = newDoc.querySelector(selector);
    const dst = document.getElementById(elId)?.querySelector(selector) ?? document.getElementById(elId);
    if (src && dst) dst.innerHTML = src.innerHTML;
  }
}

// Mirror cart.js lines 177-193: toggle is-empty on cart-items + footer, patch .js-contents in-place.
function refreshCartFooter(result, cartFooterSectionId) {
  const isEmpty = result.item_count === 0;

  // Mirror cart.js line 178: cart-items custom element controls empty-cart warning visibility
  document.querySelector('cart-items')?.classList.toggle('is-empty', isEmpty);

  const footer = document.getElementById('main-cart-footer');
  if (!footer) return;

  // Mirror cart.js line 182
  footer.classList.toggle('is-empty', isEmpty);

  // Mirror cart.js lines 185-193: patch subtotal content in-place
  if (cartFooterSectionId && result.sections?.[cartFooterSectionId]) {
    const src = new DOMParser()
      .parseFromString(result.sections[cartFooterSectionId], 'text/html')
      .querySelector('.js-contents');
    const dst = footer.querySelector('.js-contents');
    if (src && dst) dst.innerHTML = src.innerHTML;
  }
}


/* ─────────────────────────────────────────────────────────────
   2. UI
───────────────────────────────────────────────────────────── */

function buildUI(container) {
  const sectionId = container.dataset.sectionId;

  const prices = {
    text: parseInt(container.dataset.priceText, 10) || 1000,
    logo: parseInt(container.dataset.priceLogo, 10) || 1000,
    premade: parseInt(container.dataset.pricePremade, 10) || 1000,
    custom: parseInt(container.dataset.priceCustom, 10) || 5000,
  };

  let _variantWeight = parseInt(container.dataset.variantWeight, 10) || 0;

  const textPanel = document.getElementById(`pepbox-text-panel-${sectionId}`);
  function getTextChoice() {
    return container.querySelector(`input[name="pepbox-text-${sectionId}"]:checked`)?.value || 'none';
  }
  const brandPanel = document.getElementById(`pepbox-brand-panel-${sectionId}`);
  const premadePanel = document.getElementById(`pepbox-premade-panel-${sectionId}`);
  const customPanel = document.getElementById(`pepbox-custom-panel-${sectionId}`);
  const mydesignsPanel = document.getElementById(`pepbox-mydesigns-panel-${sectionId}`);
function getSubmitButton() {
    return document.getElementById(`ProductSubmitButton-${sectionId}`);
  }

  function getLogoChoice() {
    return container.querySelector(`input[name="pepbox-logo-${sectionId}"]:checked`)?.value || 'none';
  }

  function getCustomChoice() {
    return container.querySelector(`input[name="pepbox-custom-${sectionId}"]:checked`)?.value || 'none';
  }

  function getSelectedBrand() {
    return container.querySelector('.pepbox-brand-tile[aria-pressed="true"]')?.dataset.brandName || null;
  }

  function getQty() {
    const inp =
      document.querySelector(`[name="quantity"][id*="${sectionId}"]`) ||
      document.querySelector('[name="quantity"].quantity__input') ||
      document.querySelector('[name="quantity"]');
    return inp ? parseInt(inp.value, 10) || 1 : 1;
  }

  function showPanel(panel, visible) {
    if (!panel) return;
    panel.hidden = !visible;
    panel.setAttribute('aria-hidden', String(!visible));
  }

  function selectBrandTile(tile) {
    container.querySelectorAll('.pepbox-brand-tile').forEach((t) => {
      t.setAttribute('aria-pressed', 'false');
      t.classList.remove('pepbox-brand-tile--selected');
    });
    if (tile) {
      tile.setAttribute('aria-pressed', 'true');
      tile.classList.add('pepbox-brand-tile--selected');
    }
    if (selectLabel) {
      const name = tile?.dataset.brandName;
      selectLabel.textContent = name ? `Select a brand: ${name}` : 'Select a brand:';
    }
  }

  // Event delegation for dynamically-rendered brand tiles
  const brandGrid = container.querySelector(`#pepbox-brand-grid-${sectionId}`);
  brandGrid?.addEventListener('click', (e) => {
    const tile = e.target.closest('.pepbox-brand-tile');
    if (!tile || tile.classList.contains('pepbox-brand-tile--unavailable')) return;
    selectBrandTile(tile);
    updateBreakdown();
    updateSubmitGuard();
  });

  // ── Pre-made logo tiles ───────────────────────────────────────
  const premadeGrid = container.querySelector(`#pepbox-premade-grid-${sectionId}`);

  function selectPreMadeTile(tile) {
    premadeGrid?.querySelectorAll('.pepbox-premade-tile').forEach((t) => {
      t.setAttribute('aria-pressed', 'false');
      t.classList.remove('pepbox-premade-tile--selected');
    });
    if (tile) {
      tile.setAttribute('aria-pressed', 'true');
      tile.classList.add('pepbox-premade-tile--selected');
    }
    updateSubmitGuard();
  }

  function getSelectedPreMade() {
    return premadeGrid?.querySelector('.pepbox-premade-tile[aria-pressed="true"]')?.dataset.premadeName || null;
  }

  premadeGrid?.addEventListener('click', (e) => {
    const tile = e.target.closest('.pepbox-premade-tile');
    if (!tile) return;
    selectPreMadeTile(tile);
    updateBreakdown();
  });

  async function fetchAndBuildPreMadeTiles() {
    const token = container.dataset.storefrontToken;
    if (!token) return;
    const domain = window.Shopify?.shop || window.location.hostname;
    const query = `{
      metaobjects(type: "premade_logo", first: 250) {
        nodes {
          fields {
            key
            value
            reference {
              ... on MediaImage {
                image { url(transform: { maxWidth: 160, maxHeight: 160 }) altText }
              }
            }
          }
        }
      }
    }`;
    try {
      const resp = await fetch(`https://${domain}/api/2024-10/graphql.json`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Shopify-Storefront-Access-Token': token },
        body: JSON.stringify({ query }),
      });
      if (!resp.ok) return;
      const { data } = await resp.json();
      const nodes = data?.metaobjects?.nodes || [];
      if (!premadeGrid || nodes.length === 0) return;
      premadeGrid.innerHTML = '';
      nodes.forEach((node) => {
        const name = node.fields.find((f) => f.key === 'name')?.value || '';
        const logoRef = node.fields.find((f) => f.key === 'logo_image')?.reference?.image;
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'pepbox-premade-tile';
        btn.dataset.premadeName = name;
        btn.setAttribute('aria-label', name);
        btn.setAttribute('aria-pressed', 'false');
        if (logoRef?.url) {
          const img = document.createElement('img');
          img.src = logoRef.url;
          img.alt = logoRef.altText || name;
          img.className = 'pepbox-premade-tile__logo';
          img.loading = 'lazy';
          btn.appendChild(img);
        }
        const span = document.createElement('span');
        span.className = 'pepbox-premade-tile__name';
        span.textContent = name;
        btn.appendChild(span);
        premadeGrid.appendChild(btn);
      });
    } catch (err) {
      console.error('[PepBox] Failed to load pre-made logos:', err);
    }
  }

  // ── Saved custom designs (returning customers) ─────────────────
  // Tiles are rendered server-side in Liquid from the customer's own
  // approved_designs metafield — no fetch needed here. Liquid resolves that
  // "list of metaobject references" metafield, including the nested
  // artwork file reference, natively for the currently authenticated
  // customer, so there's nothing left for JS to do but wire up selection
  // on whatever tiles are already in the DOM.
  const mydesignsGrid = container.querySelector(`#pepbox-mydesigns-grid-${sectionId}`);

  function selectMyDesignTile(tile) {
    mydesignsGrid?.querySelectorAll('.pepbox-mydesigns-tile').forEach((t) => {
      t.setAttribute('aria-pressed', 'false');
      t.classList.remove('pepbox-mydesigns-tile--selected');
    });
    if (tile) {
      tile.setAttribute('aria-pressed', 'true');
      tile.classList.add('pepbox-mydesigns-tile--selected');
    }
    updateSubmitGuard();
  }

  function getSelectedMyDesign() {
    const tile = mydesignsGrid?.querySelector('.pepbox-mydesigns-tile[aria-pressed="true"]');
    if (!tile) return null;
    return {
      jobId: tile.dataset.designJobId || null,
      logoUrl: tile.dataset.logoUrl || null,
      label: tile.dataset.designLabel || null,
    };
  }

  mydesignsGrid?.addEventListener('click', (e) => {
    const tile = e.target.closest('.pepbox-mydesigns-tile');
    if (!tile) return;
    selectMyDesignTile(tile);
    updateBreakdown();
  });

  // ── Brand access code unlock (Storefront API) ─────────────────
  const storefrontToken = container.dataset.storefrontToken;
  const shopDomain = window.Shopify?.shop || window.location.hostname;
  const accessToggle = container.querySelector(`#pepbox-brand-access-toggle-${sectionId}`);
  const accessForm = container.querySelector(`#pepbox-brand-access-form-${sectionId}`);
  const codeInput = container.querySelector(`#pepbox-brand-code-${sectionId}`);
  const unlockBtn = container.querySelector('.pepbox-brand-access__btn');
  const accessStatus = container.querySelector(`#pepbox-brand-access-status-${sectionId}`);
  const selectLabel = container.querySelector('.pepbox-brand-panel__select-label');

  const CODES_KEY = 'pepbox_brand_codes';
  let _unlockedBrands = []; // accumulated brand nodes across all applied codes
  const _appliedCodes = new Set();

  function getStoredCodes() {
    try { return JSON.parse(sessionStorage.getItem(CODES_KEY) || '[]'); } catch { return []; }
  }

  function persistCode(code) {
    const codes = getStoredCodes();
    if (!codes.includes(code)) {
      codes.push(code);
      sessionStorage.setItem(CODES_KEY, JSON.stringify(codes));
    }
  }

  accessToggle?.addEventListener('click', () => {
    const expanded = accessToggle.getAttribute('aria-expanded') === 'true';
    accessToggle.setAttribute('aria-expanded', String(!expanded));
    if (accessForm) accessForm.hidden = expanded;
    if (expanded && codeInput) codeInput.value = '';
  });

  function setAccessStatus(message, type) {
    if (!accessStatus) return;
    accessStatus.hidden = !message;
    accessStatus.textContent = message || '';
    accessStatus.className = `pepbox-brand-access__status pepbox-brand-access__status--${type}`;
  }

  function collapseAfterUnlock() {
    if (accessForm) accessForm.hidden = true;
    if (accessToggle) accessToggle.setAttribute('aria-expanded', 'false');
    if (codeInput) codeInput.value = '';
    const total = _unlockedBrands.length;
    setAccessStatus(`✓ ${total} brand${total > 1 ? 's' : ''} unlocked`, 'success');
  }

  async function fetchBrandsForCode(code) {
    if (!storefrontToken) return null;
    const query = `{
      metaobjects(type: "brand_access_code", first: 250) {
        nodes {
          fields {
            key
            value
            references(first: 20) {
              nodes {
                ... on Metaobject {
                  fields {
                    key
                    value
                    reference {
                      ... on MediaImage {
                        image { url(transform: { maxWidth: 160, maxHeight: 160 }) altText }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }`;

    const resp = await fetch(`https://${shopDomain}/api/2024-10/graphql.json`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': storefrontToken,
      },
      body: JSON.stringify({ query }),
    });

    if (!resp.ok) return null;
    const { data } = await resp.json();
    const lc = code.toLowerCase().trim();

    const match = data?.metaobjects?.nodes?.find((node) => {
      const codeField = node.fields.find((f) => f.key === 'code');
      return (codeField?.value || '').toLowerCase() === lc;
    });

    if (!match) return null;
    const brandsField = match.fields.find((f) => f.key === 'brands');
    return brandsField?.references?.nodes || null;
  }

  function mergeBrands(newBrands) {
    const existingNames = new Set(
      _unlockedBrands.map((n) => n.fields.find((f) => f.key === 'brand_name')?.value)
    );
    for (const brand of newBrands) {
      const name = brand.fields.find((f) => f.key === 'brand_name')?.value;
      if (name && !existingNames.has(name)) {
        _unlockedBrands.push(brand);
        existingNames.add(name);
      }
    }
  }

  function buildBrandTiles() {
    if (!brandGrid) return;
    brandGrid.innerHTML = '';
    const sortedBrands = [..._unlockedBrands].sort((a, b) => {
      const nameA = a.fields.find((f) => f.key === 'brand_name')?.value || '';
      const nameB = b.fields.find((f) => f.key === 'brand_name')?.value || '';
      return nameA.localeCompare(nameB);
    });
    sortedBrands.forEach((node) => {
      const nameField = node.fields.find((f) => f.key === 'brand_name');
      const name = nameField?.value || '';
      const logoField = node.fields.find((f) => f.key === 'brand_logo');
      const logoImage = logoField?.reference?.image;

      // sizing — list of metaobject references with name, min_weight, max_weight fields
      const sizingField = node.fields.find((f) => f.key === 'sizing');
      const sizingRanges = (sizingField?.references?.nodes || []).map((n) => {
        const get = (k) => n.fields?.find((f) => f.key === k)?.value;
        const maxVal = get('max_weight');
        return { name: get('name'), min: parseFloat(get('min_weight') || 0), max: maxVal != null ? parseFloat(maxVal) : null };
      });

      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'pepbox-brand-tile';
      btn.dataset.brandName = name;
      btn.dataset.sizingRanges = JSON.stringify(sizingRanges);
      btn.setAttribute('aria-label', name);
      btn.setAttribute('aria-pressed', 'false');

      if (logoImage?.url) {
        const img = document.createElement('img');
        img.src = logoImage.url;
        img.alt = logoImage.altText || name;
        img.className = 'pepbox-brand-tile__logo';
        img.loading = 'lazy';
        btn.appendChild(img);
      }

      const span = document.createElement('span');
      span.className = 'pepbox-brand-tile__name';
      span.textContent = name;
      btn.appendChild(span);

      brandGrid.appendChild(btn);
    });
    if (_unlockedBrands.length > 0) {
      if (selectLabel) selectLabel.hidden = false;
      updateBrandRadioVisibility();
    }
    // Re-apply weight filter immediately if a brand radio is already selected
    if (getLogoChoice() === 'brand') {
      filterBrandTiles();
    }
  }

  async function applyCode(code, silent = false) {
    const lc = code.toLowerCase().trim();
    if (_appliedCodes.has(lc)) return;

    if (!silent) {
      setAccessStatus('Checking code…', 'loading');
      if (unlockBtn) unlockBtn.disabled = true;
    }
    try {
      const brands = await fetchBrandsForCode(lc);
      if (brands && brands.length > 0) {
        _appliedCodes.add(lc);
        persistCode(lc);
        mergeBrands(brands);
        buildBrandTiles();
        if (!silent) collapseAfterUnlock();
      } else {
        if (!silent) setAccessStatus('Code not recognised. Please check and try again.', 'error');
      }
    } catch {
      if (!silent) setAccessStatus('Could not verify code. Please try again.', 'error');
    } finally {
      if (!silent && unlockBtn) unlockBtn.disabled = false;
    }
  }

  unlockBtn?.addEventListener('click', () => {
    const code = codeInput?.value.trim();
    if (code) applyCode(code);
  });

  codeInput?.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') { e.preventDefault(); unlockBtn?.click(); }
  });

  // Pre-made logos are fetched on first selection, not at init
  let _premadeFetched = false;

  // Auto-apply from URL param and/or sessionStorage on init
  const urlCode = new URLSearchParams(window.location.search).get('brand-access');
  const allInitCodes = [...new Set([
    ...getStoredCodes(),
    ...(urlCode ? [urlCode.toLowerCase().trim()] : []),
  ])];
  if (urlCode) persistCode(urlCode.toLowerCase().trim());
  if (allInitCodes.length > 0) {
    Promise.all(allInitCodes.map((c) => applyCode(c, true))).then(() => {
      if (_unlockedBrands.length > 0) collapseAfterUnlock();
    });
  }

  function updateSubmitGuard() {
    const btn = getSubmitButton();
    if (!btn) return;
    const logo = getLogoChoice();
    const custom = getCustomChoice();
    if (getTextChoice() === 'text') {
      const textInput = document.getElementById(`pepbox-text-input-${sectionId}`);
      const hasText = !!(textInput?.value.trim());
      if (!hasText) {
        btn.disabled = true;
        btn.title = 'Please enter your personalisation text before adding to cart';
        return;
      }
    }
    if (custom === 'custom') {
      const hasUpload = document.getElementById(`pepbox-upload-status-${sectionId}`)?.hidden === false;
      btn.disabled = !hasUpload;
      btn.title = hasUpload ? '' : 'Please upload your logo file before adding to cart';
    } else if (logo === 'brand') {
      const hasSelection = !!container.querySelector('.pepbox-brand-tile[aria-pressed="true"]:not(.pepbox-brand-tile--unavailable)');
      btn.disabled = !hasSelection;
      btn.title = hasSelection ? '' : 'Please select a brand logo before adding to cart';
    } else if (logo === 'premade') {
      const hasSelection = !!premadeGrid?.querySelector('.pepbox-premade-tile[aria-pressed="true"]');
      btn.disabled = !hasSelection;
      btn.title = hasSelection ? '' : 'Please select a pre-made logo before adding to cart';
    } else if (logo === 'mydesigns') {
      const hasSelection = !!mydesignsGrid?.querySelector('.pepbox-mydesigns-tile[aria-pressed="true"]');
      btn.disabled = !hasSelection;
      btn.title = hasSelection ? '' : 'Please select a saved design before adding to cart';
    } else {
      btn.disabled = false;
      btn.title = '';
    }
  }

  function updateBreakdown() {
    const logo = getLogoChoice();
    const custom = getCustomChoice();
    const qty = getQty();
    const addonRows = [];

    if (getTextChoice() === 'text') {
      addonRows.push({ label: `Text personalisation × ${qty}`, amount: prices.text * qty });
    }
    if (logo === 'brand') addonRows.push({ label: `Brand Logo × ${qty}`, amount: prices.logo * qty });
    else if (logo === 'premade') addonRows.push({ label: `Pre-Made Logo × ${qty}`, amount: prices.premade * qty });
    else if (logo === 'mydesigns') addonRows.push({ label: 'Custom Logo (saved design)', amount: 0 });
    if (custom === 'custom') addonRows.push({ label: 'Custom logo design (one-off)', amount: prices.custom });

    // Notify order summary block of current add-on state
    document.dispatchEvent(new CustomEvent('pepbox:addons-changed', {
      detail: { sectionId, rows: addonRows },
    }));
  }

  function filterBrandTiles() {
    const tiles = container.querySelectorAll('.pepbox-brand-tile');
    const sizeNote = document.getElementById(`pepbox-brand-size-note-${sectionId}`);
    let anyUnavailable = false;

    tiles.forEach((tile) => {
      // Size filter — does the current variant weight fall within the brand's sizing range?
      let sizingRanges;
      try { sizingRanges = JSON.parse(tile.dataset.sizingRanges || '[]'); } catch (e) { sizingRanges = []; }
      const available = sizingRanges.length === 0 ||
        sizingRanges.some((r) => _variantWeight >= r.min && _variantWeight <= (r.max ?? Infinity));

      tile.classList.toggle('pepbox-brand-tile--unavailable', !available);
      tile.setAttribute('aria-disabled', String(!available));

      // Deselect the tile if it just became unavailable
      if (!available && tile.getAttribute('aria-pressed') === 'true') {
        tile.setAttribute('aria-pressed', 'false');
        tile.classList.remove('pepbox-brand-tile--selected');
        if (selectLabel) selectLabel.textContent = 'Select a brand:';
      }

      if (!available) anyUnavailable = true;
    });

    if (sizeNote) sizeNote.hidden = !anyUnavailable;
    updateSubmitGuard();
  }

  function updateBrandRadioVisibility() {
    const tiles = container.querySelectorAll('.pepbox-brand-tile');
    if (tiles.length === 0) return;

    let anyAvailable = false;

    tiles.forEach((tile) => {
      let sizingRanges;
      try { sizingRanges = JSON.parse(tile.dataset.sizingRanges || '[]'); } catch (e) { sizingRanges = []; }
      const weightOk = sizingRanges.length === 0 ||
        sizingRanges.some((r) => _variantWeight >= r.min && _variantWeight <= (r.max ?? Infinity));
      if (weightOk) anyAvailable = true;
    });

    const brandOption = container.querySelector('input[value="brand"]')?.closest('.pepbox-brand-logo-option');
    if (brandOption) brandOption.hidden = !anyAvailable;

    // If the selected option just became hidden, reset to none
    if (getLogoChoice() === 'brand' && !anyAvailable) {
      const noneRadio = container.querySelector(`input[name="pepbox-logo-${sectionId}"][value="none"]`);
      if (noneRadio) {
        noneRadio.checked = true;
        container.querySelectorAll('.pepbox-radio-label').forEach((l) => l.classList.remove('pepbox-radio-label--active'));
        noneRadio.closest('.pepbox-radio-label')?.classList.add('pepbox-radio-label--active');
        updatePanels();
      }
    }
  }

  function updatePanels() {
    const logo = getLogoChoice();
    const custom = getCustomChoice();
    const textWasHidden = textPanel?.hidden !== false;
    showPanel(textPanel, getTextChoice() === 'text');
    if (textWasHidden && getTextChoice() === 'text') {
      // Small delay so the panel is visible before focus is applied
      setTimeout(() => document.getElementById(`pepbox-text-input-${sectionId}`)?.focus(), 50);
    }
    showPanel(brandPanel, logo === 'brand');
    showPanel(premadePanel, logo === 'premade');
    showPanel(mydesignsPanel, logo === 'mydesigns');
    showPanel(customPanel, custom === 'custom');
    if (logo === 'brand') filterBrandTiles();
    updateBreakdown();
    updateSubmitGuard();
  }

  // Selecting an option in one radio group resets the other to 'none' — the
  // Logo card (apply an existing image) and Custom Image card (commission a
  // new one-off design) are mutually exclusive per box.
  function selectRadioNone(groupName) {
    const noneRadio = container.querySelector(`input[name="${groupName}-${sectionId}"][value="none"]`);
    if (!noneRadio || noneRadio.checked) return;
    noneRadio.checked = true;
    noneRadio.closest('.pepbox-radio-group')?.querySelectorAll('.pepbox-radio-label')
      .forEach((l) => l.classList.remove('pepbox-radio-label--active'));
    noneRadio.closest('.pepbox-radio-label')?.classList.add('pepbox-radio-label--active');
  }

  container.querySelectorAll(`input[name="pepbox-text-${sectionId}"]`).forEach((radio) => {
    radio.addEventListener('change', () => {
      radio.closest('.pepbox-radio-group')?.querySelectorAll('.pepbox-radio-label')
        .forEach((l) => l.classList.remove('pepbox-radio-label--active'));
      radio.closest('.pepbox-radio-label')?.classList.add('pepbox-radio-label--active');
      if (radio.value === 'none') setTextError(false);
      updatePanels();
    });
  });

  const _textInput = document.getElementById(`pepbox-text-input-${sectionId}`);
  const _textError = document.getElementById(`pepbox-text-error-${sectionId}`);

  function setTextError(show) {
    if (_textInput) _textInput.classList.toggle('pepbox-text-input--error', show);
    if (_textError) _textError.hidden = !show;
  }

  _textInput?.addEventListener('input', () => {
    if (_textInput.value.trim()) setTextError(false);
    updateSubmitGuard();
  });

  _textInput?.addEventListener('blur', () => {
    if (getTextChoice() === 'text' && !_textInput.value.trim()) setTextError(true);
  });

  container.querySelectorAll(`input[name="pepbox-logo-${sectionId}"]`).forEach((radio) => {
    radio.addEventListener('change', () => {
      radio.closest('.pepbox-radio-group')?.querySelectorAll('.pepbox-radio-label')
        .forEach((l) => l.classList.remove('pepbox-radio-label--active'));
      radio.closest('.pepbox-radio-label')?.classList.add('pepbox-radio-label--active');

      if (radio.value !== 'none') selectRadioNone('pepbox-custom');

      updatePanels();

      // Auto-select first available brand tile after filtering
      if (radio.value === 'brand') {
        const firstAvailable = Array.from(container.querySelectorAll('.pepbox-brand-tile'))
          .find((t) => !t.classList.contains('pepbox-brand-tile--unavailable'));
        selectBrandTile(firstAvailable || null);
        updateSubmitGuard();
      }
      if (radio.value === 'premade') {
        if (!_premadeFetched) {
          _premadeFetched = true;
          fetchAndBuildPreMadeTiles().then(() => updateSubmitGuard());
        } else {
          updateSubmitGuard();
        }
      }
    });
  });

  container.querySelectorAll(`input[name="pepbox-custom-${sectionId}"]`).forEach((radio) => {
    radio.addEventListener('change', () => {
      radio.closest('.pepbox-radio-group')?.querySelectorAll('.pepbox-radio-label')
        .forEach((l) => l.classList.remove('pepbox-radio-label--active'));
      radio.closest('.pepbox-radio-label')?.classList.add('pepbox-radio-label--active');

      if (radio.value !== 'none') selectRadioNone('pepbox-logo');

      updatePanels();
    });
  });

  const qtyInput =
    document.querySelector(`[name="quantity"][id*="${sectionId}"]`) ||
    document.querySelector('[name="quantity"].quantity__input') ||
    document.querySelector('[name="quantity"]');
  if (qtyInput) qtyInput.addEventListener('change', updateBreakdown);

  updatePanels();

  function setVariantWeight(grams) {
    _variantWeight = grams;
    const logo = getLogoChoice();
    if (logo === 'brand') filterBrandTiles();
    updateBrandRadioVisibility();
  }

  return { getTextChoice, getLogoChoice, getCustomChoice, getSelectedBrand, getSelectedPreMade, getSelectedMyDesign, getQty, updateBreakdown, updateSubmitGuard, setVariantWeight, setTextError, prices };
}

/* ─────────────────────────────────────────────────────────────
   3. UPLOAD
───────────────────────────────────────────────────────────── */

function buildUpload(container, uiRef) {
  const sectionId = container.dataset.sectionId;
  const uploadBtn = document.getElementById(`pepbox-upload-btn-${sectionId}`);
  const uploadStatus = document.getElementById(`pepbox-upload-status-${sectionId}`);
  const statusName = uploadStatus?.querySelector('.pepbox-upload-status__name');
  const removeBtn = uploadStatus?.querySelector('.pepbox-upload-status__remove');

  let _uploadedUrl = null;
  let _widget = null;

  // Native file input — used as fallback when Cloudinary widget is not loaded (e.g. local dev).
  // In production with Cloudinary configured, this is never triggered.
  const _fallbackInput = document.createElement('input');
  _fallbackInput.type = 'file';
  _fallbackInput.accept = '.png,.jpg,.jpeg,.svg,.pdf';
  _fallbackInput.style.display = 'none';
  document.body.appendChild(_fallbackInput);

  _fallbackInput.addEventListener('change', () => {
    const file = _fallbackInput.files[0];
    if (!file) return;
    _uploadedUrl = URL.createObjectURL(file);
    const dotIdx = file.name.lastIndexOf('.');
    if (statusName) statusName.textContent = file.name;
    if (uploadStatus) { uploadStatus.hidden = false; uploadStatus.removeAttribute('hidden'); }
    uiRef.updateSubmitGuard();
    uiRef.updateBreakdown();
    _fallbackInput.value = '';
  });

  function _createWidget() {
    if (!window.cloudinary) return null;
    return window.cloudinary.createUploadWidget(
      {
        cloudName: container.dataset.cloudinaryCloud,
        uploadPreset: container.dataset.cloudinaryPreset,
        sources: ['local', 'url', 'camera'],
        multiple: false,
        maxFileSize: 10485760,
        clientAllowedFormats: ['png', 'jpg', 'jpeg', 'svg', 'pdf'],
        cropping: false,
        showPoweredBy: false,
      },
      (error, result) => {
        if (error) { console.error('[PepBox] Cloudinary error:', error); return; }
        if (result.event === 'success') {
          _uploadedUrl = result.info.secure_url;
          const ext = result.info.format ? `.${result.info.format}` : '';
          if (statusName) statusName.textContent = (result.info.original_filename || 'file') + ext;
          if (uploadStatus) { uploadStatus.hidden = false; uploadStatus.removeAttribute('hidden'); }
          uiRef.updateSubmitGuard();
          uiRef.updateBreakdown();
        }
      }
    );
  }

  function clearUpload() {
    if (_uploadedUrl?.startsWith('blob:')) URL.revokeObjectURL(_uploadedUrl);
    _uploadedUrl = null;
    if (uploadStatus) uploadStatus.hidden = true;
    if (statusName) statusName.textContent = '';
    uiRef.updateSubmitGuard();
  }

  uploadBtn?.addEventListener('click', () => {
    const cloudName = container.dataset.cloudinaryCloud;
    const preset = container.dataset.cloudinaryPreset;
    if (!window.cloudinary || !cloudName || !preset) { _fallbackInput.click(); return; }
    if (!_widget) _widget = _createWidget();
    _widget?.open();
  });

  removeBtn?.addEventListener('click', clearUpload);

  return { getUploadedUrl: () => _uploadedUrl, clearUpload };
}

/* ─────────────────────────────────────────────────────────────
   4. CART MANAGER
   Text, Brand Logo, Pre-Made Logo, and the Custom Logo Design fee
   are all added as separate line items linked to the box via
   _bundle_id. The design fee also carries _design_job_id so
   SyncManager keeps it at quantity 1 regardless of box quantity.
   Reusing a saved design ('mydesigns') is box-properties only —
   no addon line item, since no new fee is charged.
───────────────────────────────────────────────────────────── */

function buildCartManager(container, uiRef, uploadRef) {
  const sectionId = container.dataset.sectionId;

  const variantIds = {
    text: container.dataset.textVariant,
    logo: container.dataset.logoVariant,
    premade: container.dataset.premadeVariant,
    custom: container.dataset.customVariant,
  };

  async function _handleAddToCart(e) {
    e.preventDefault();
    e.stopImmediatePropagation();

    const form = e.currentTarget || e.target;

    // Run any validators registered by co-located pickers (e.g. vault-layer-picker)
    const extraValidators = form._pepboxValidators || [];
    if (extraValidators.some(v => !v())) return;

    const boxVariantId = new FormData(form).get('id');
    const qty = uiRef.getQty();
    const logoChoice = uiRef.getLogoChoice();
    const customChoice = uiRef.getCustomChoice();
    const textInput = document.getElementById(`pepbox-text-input-${sectionId}`);
    const hasText = !!(uiRef.getTextChoice() === 'text' && textInput?.value.trim());
    const hasAddon = hasText || logoChoice === 'brand' || logoChoice === 'premade' || customChoice === 'custom';

    // Only generate a bundle id when an add-on line item will actually need to link back to this box.
    const bundleId = hasAddon ? uuidV4() : null;

    // Box properties (shown in cart)
    const boxProperties = {};
    if (bundleId) boxProperties['_bundle_id'] = bundleId;
    if (hasText) boxProperties['Lid Text'] = textInput.value.trim();
    if (logoChoice === 'brand') {
      boxProperties['Logo'] = 'Brand Logo';
      const brand = uiRef.getSelectedBrand();
      if (brand) boxProperties['Brand'] = brand;
    } else if (logoChoice === 'premade') {
      boxProperties['Logo'] = 'Pre-Made Logo';
      const premade = uiRef.getSelectedPreMade();
      if (premade) boxProperties['Pre-Made Logo'] = premade;
    } else if (logoChoice === 'mydesigns') {
      // Reusing a previously approved design — no new fee, no addon line item.
      // 'Logo File' is a plain (non-underscore) property, and Shopify renders an
      // image-URL property value as a thumbnail in cart/checkout/order emails
      // automatically, so this is what makes the selected design visible at
      // checkout without any custom checkout code.
      boxProperties['Logo'] = 'Custom Logo Design';
      const design = uiRef.getSelectedMyDesign();
      if (design?.logoUrl) boxProperties['Logo File'] = design.logoUrl;
      if (design?.jobId) boxProperties['_design_job_id'] = design.jobId;
    } else if (customChoice === 'custom') {
      boxProperties['Logo'] = 'Custom Logo Design';
      const url = uploadRef.getUploadedUrl();
      if (url) boxProperties['Logo File'] = url;
    }

    // Merge any hidden properties[*] inputs injected by other pickers (e.g. vault-layer-picker)
    form.querySelectorAll('input[type="hidden"][name^="properties["]').forEach(input => {
      if (input.value) {
        const key = input.name.slice('properties['.length, -1);
        boxProperties[key] = input.value;
      }
    });

    const submitBtn = document.getElementById(`ProductSubmitButton-${sectionId}`);
    const origText = submitBtn?.textContent || '';
    if (submitBtn) { submitBtn.disabled = true; submitBtn.textContent = 'Adding…'; }

    try {
      const itemsToAdd = [{ id: boxVariantId, quantity: qty, properties: boxProperties }];

      // Per-box personalisation add-ons
      if (hasText && variantIds.text) {
        itemsToAdd.push({
          id: variantIds.text, quantity: qty,
          properties: { _bundle_id: bundleId, _is_addon: 'true', 'Lid Text': textInput.value.trim() },
        });
      }
      if (logoChoice === 'brand' && variantIds.logo) {
        itemsToAdd.push({
          id: variantIds.logo, quantity: qty,
          properties: { _bundle_id: bundleId, _is_addon: 'true', Brand: uiRef.getSelectedBrand() || '' },
        });
      }
      if (logoChoice === 'premade' && variantIds.premade) {
        itemsToAdd.push({
          id: variantIds.premade, quantity: qty,
          properties: { _bundle_id: bundleId, _is_addon: 'true', 'Pre-Made Logo': uiRef.getSelectedPreMade() || '' },
        });
      }

      // Custom logo design fee — one-off, quantity always 1, linked via _design_job_id
      if (customChoice === 'custom' && variantIds.custom) {
        const designJobId = uuidV4();
        boxProperties['_design_job_id'] = designJobId;
        itemsToAdd[0].properties = boxProperties;
        itemsToAdd.push({
          id: variantIds.custom, quantity: 1,
          properties: { _bundle_id: bundleId, _design_job_id: designJobId, _is_addon: 'true' },
        });
        // Flag the cart so staff know to start the design work
        await cartFetch('update', { note: 'Custom logo design requested — PepBox team to begin design work.' });
      }

      const addResponse = await cartFetch('add', {
        items: itemsToAdd,
        sections: ['cart-notification-product', 'cart-notification-button', 'cart-icon-bubble'],
      });
      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));

      // Trigger the cart notification manually — our form intercept bypasses Dawn's native trigger.
      // Pass the box item's key so the notification renders the correct product card.
      const cartNotification = document.querySelector('cart-notification');
      const boxItemKey = addResponse.items?.[0]?.key;
      if (cartNotification?.renderContents && boxItemKey && addResponse.sections) {
        cartNotification.renderContents({ key: boxItemKey, sections: addResponse.sections });
      }

      // Reset form UI
      if (textInput) textInput.value = '';
      uiRef.setTextError(false);
      const noneTextRadio = container.querySelector(`input[name="pepbox-text-${sectionId}"][value="none"]`);
      if (noneTextRadio) {
        noneTextRadio.checked = true;
        noneTextRadio.closest('.pepbox-radio-label')?.classList.add('pepbox-radio-label--active');
      }
      const noneRadio = container.querySelector(`input[name="pepbox-logo-${sectionId}"][value="none"]`);
      if (noneRadio) {
        noneRadio.checked = true;
        noneRadio.closest('.pepbox-radio-group')?.querySelectorAll('.pepbox-radio-label')
          .forEach((l) => l.classList.remove('pepbox-radio-label--active'));
        noneRadio.closest('.pepbox-radio-label')?.classList.add('pepbox-radio-label--active');
      }
      const noneCustomRadio = container.querySelector(`input[name="pepbox-custom-${sectionId}"][value="none"]`);
      if (noneCustomRadio) {
        noneCustomRadio.checked = true;
        noneCustomRadio.closest('.pepbox-radio-group')?.querySelectorAll('.pepbox-radio-label')
          .forEach((l) => l.classList.remove('pepbox-radio-label--active'));
        noneCustomRadio.closest('.pepbox-radio-label')?.classList.add('pepbox-radio-label--active');
      }
      uploadRef.clearUpload();
      ['text', 'brand', 'premade', 'mydesigns', 'custom'].forEach((n) => {
        const p = document.getElementById(`pepbox-${n}-panel-${sectionId}`);
        if (p) { p.hidden = true; p.setAttribute('aria-hidden', 'true'); }
      });
    } catch (err) {
      console.error('[PepBox] Add to cart failed:', err);
      alert('Something went wrong adding your personalisation. Please try again.');
    } finally {
      if (submitBtn) { submitBtn.disabled = false; submitBtn.textContent = origText; uiRef.updateSubmitGuard(); }
    }
  }

  function attach() {
    const productForm =
      document.getElementById(`product-form-${sectionId}`) ||
      document.querySelector(`[data-section-id="${sectionId}"] form[action="/cart/add"]`) ||
      document.querySelector(`form[action="/cart/add"]`);
    if (!productForm) { console.warn('[PepBox] Product form not found for section', sectionId); return; }
    productForm.addEventListener('submit', _handleAddToCart, true);
  }

  return { attach, variantIds };
}

/* ─────────────────────────────────────────────────────────────
   5. SYNC MANAGER
   Triggered by a fetch interceptor on /cart/change.js calls
   (Dawn's cart page +/- buttons) so it fires reliably without
   depending on specific theme event names.
───────────────────────────────────────────────────────────── */

const SyncManager = (() => {
  let _isSyncing = false;

  async function sync(loadingEl) {
    if (_isSyncing) return;
    _isSyncing = true;

    try {
      const cart = await getCart();
      const items = cart.items;

      // Build maps from box items (anything without _is_addon: 'true')
      const bundleQtyMap = {};        // bundleId → box qty
      const activeJobIds = new Set(); // _design_job_id values still referenced by a box

      for (const item of items) {
        if (item.properties?.['_is_addon'] === 'true') continue;
        const bundleId = item.properties?.['_bundle_id'];
        if (bundleId) bundleQtyMap[bundleId] = (bundleQtyMap[bundleId] || 0) + item.quantity;
        const jobId = item.properties?.['_design_job_id'];
        if (jobId) activeJobIds.add(jobId);
      }

      const updates = {};

      for (const item of items) {
        if (item.properties?.['_is_addon'] !== 'true') continue;

        const jobId = item.properties?.['_design_job_id'];
        if (jobId) {
          // Design fee: keep at 1 if job still referenced by a box, else remove
          if (!activeJobIds.has(jobId)) updates[item.key] = 0;
          else if (item.quantity !== 1) updates[item.key] = 1;
        } else {
          // Text / brand logo: sync qty to match box qty
          const bundleId = item.properties?.['_bundle_id'];
          if (!bundleId || !bundleQtyMap[bundleId]) updates[item.key] = 0;
          else if (item.quantity !== bundleQtyMap[bundleId]) updates[item.key] = bundleQtyMap[bundleId];
        }
      }

      if (Object.keys(updates).length > 0) {
        // Read section IDs from dataset.id — mirrors cart.js getSectionsToRender() lines 122-145.
        // templates/cart.json keys ('cart-items', 'cart-footer') differ from HTML element IDs.
        const cartItemsSectionId = document.getElementById('main-cart-items')?.dataset.id;
        const cartFooterSectionId = document.getElementById('main-cart-footer')?.dataset.id;

        const result = await cartFetch('update', {
          updates,
          sections: [cartItemsSectionId, cartFooterSectionId, 'cart-icon-bubble', 'cart-live-region-text'].filter(Boolean),
          sections_url: window.location.pathname,
        });
        renderCartSections(result.sections, cartItemsSectionId);
        refreshCartFooter(result, cartFooterSectionId);
      }
    } catch (err) {
      console.error('[PepBox] Sync failed:', err);
    } finally {
      _isSyncing = false;
      loadingEl?.removeAttribute('aria-busy');
    }
  }

  return { sync, get isSyncing() { return _isSyncing; } };
})();

/* ─────────────────────────────────────────────────────────────
   6. ORDER SUMMARY
   Independent product page block. Always visible. Shows base
   product price × qty plus any active personalisation add-ons.
   Listens for pepbox:addons-changed events from the buildUI
   updateBreakdown function, and updates when variant or qty changes.
───────────────────────────────────────────────────────────── */

function buildOrderSummary(container) {
  const sectionId = container.dataset.sectionId;
  const moneyFormat = container.dataset.moneyFormat;
  const rowsEl = document.getElementById(`pepbox-order-summary-rows-${sectionId}`);

  // Build variant price lookup from embedded JSON
  const variantDataEl = document.getElementById(`pepbox-variant-data-${sectionId}`);
  const variantMap = {};
  try {
    const data = JSON.parse(variantDataEl?.textContent || '{}');
    (data.variants || []).forEach((v) => { variantMap[v.id] = v; });
  } catch (e) { }

  let _addonRows = [];
  let _basePrice = parseInt(container.dataset.basePrice, 10) || 0;
  let _discountCents = 0;

  function getQty() {
    const inp =
      document.querySelector(`[name="quantity"][id*="${sectionId}"]`) ||
      document.querySelector('[name="quantity"].quantity__input') ||
      document.querySelector('[name="quantity"]');
    return inp ? parseInt(inp.value, 10) || 1 : 1;
  }

  // Update the main page price element to reflect the colour discount.
  // Safe to call after every variant-change since Dawn re-renders the price element fresh
  // before our PubSub callback fires, so there is nothing to undo.
  function applyDiscountToPagePrice() {
    const priceWrapper = document.getElementById(`price-${sectionId}`);
    const priceEl = priceWrapper?.querySelector('.price');
    if (!priceEl) return;

    if (_discountCents > 0) {
      const discountedPrice = _basePrice - _discountCents;
      const strikeEl = priceEl.querySelector('.price__sale s');
      const salePriceEl = priceEl.querySelector('.price-item--sale');
      if (strikeEl) strikeEl.textContent = formatMoney(_basePrice, moneyFormat);
      if (salePriceEl) salePriceEl.textContent = formatMoney(discountedPrice, moneyFormat);
      priceEl.classList.add('price--on-sale');
      priceEl.dataset.pepboxDiscount = '1';
    } else if (priceEl.dataset.pepboxDiscount) {
      priceEl.classList.remove('price--on-sale');
      delete priceEl.dataset.pepboxDiscount;
    }
  }

  function render() {
    if (!rowsEl) return;
    const qty = getQty();
    const productTitle = container.dataset.productTitle || 'Product';
    const rows = [{ label: `${productTitle} × ${qty}`, amount: _basePrice * qty }];

    if (_discountCents > 0) {
      rows.push({ label: 'Colour discount', amount: -_discountCents * qty, isDiscount: true });
    }

    _addonRows.forEach((r) => rows.push(r));
    const grandTotal = rows.reduce((s, r) => s + r.amount, 0);
    rows.push({ label: 'Order total', amount: grandTotal, isTotal: true });

    rowsEl.innerHTML = rows.map((r) => {
      const amountStr = r.isDiscount
        ? `−${formatMoney(Math.abs(r.amount), moneyFormat)}`
        : formatMoney(r.amount, moneyFormat);
      return `<div class="pepbox-order-summary__row${r.isTotal ? ' pepbox-order-summary__row--total' : ''}${r.isDiscount ? ' pepbox-order-summary__row--discount' : ''}">
        <span class="pepbox-order-summary__label">${r.label}</span>
        <span class="pepbox-order-summary__amount">${amountStr}</span>
      </div>`;
    }).join('');

    applyDiscountToPagePrice();
  }

  // Add-on changes from personalisation block
  document.addEventListener('pepbox:addons-changed', (e) => {
    if (e.detail?.sectionId !== sectionId) return;
    _addonRows = e.detail.rows || [];
    render();
  });

  // Variant change — subscribe to Dawn's PubSub event, fired after the full section update completes.
  // The event carries the variant object directly so we don't need to read the DOM.
  if (typeof subscribe === 'function') {
    subscribe('variant-change', (event) => {
      const variant = event?.data?.variant;
      if (event?.data?.sectionId !== sectionId || !variant) return;
      if (variantMap[variant.id]) {
        _basePrice = variantMap[variant.id].price;
        _discountCents = variantMap[variant.id].discountCents || 0;
      } else {
        _basePrice = variant.price ?? _basePrice;
        _discountCents = 0;
      }
      render();
    });
  }

  // Qty change
  const qtyInput =
    document.querySelector(`[name="quantity"][id*="${sectionId}"]`) ||
    document.querySelector('[name="quantity"].quantity__input') ||
    document.querySelector('[name="quantity"]');
  qtyInput?.addEventListener('change', render);

  // Initialise discount from the already-selected variant on page load
  const selectedVariantId = parseInt(container.dataset.selectedVariantId, 10);
  if (selectedVariantId && variantMap[selectedVariantId]) {
    _discountCents = variantMap[selectedVariantId].discountCents || 0;
  }

  render();
}

/* ─────────────────────────────────────────────────────────────
   7. BOOT
───────────────────────────────────────────────────────────── */

document.addEventListener('DOMContentLoaded', () => {
  // Intercept /cart/change.js on every page (including the cart page).
  // Our sync uses /cart/update.js so there is no loop between the two.
  const cartItemsEl = document.querySelector('cart-items');
  const _origFetch = window.fetch;
  window.fetch = async function (resource, init) {
    const response = await _origFetch.apply(this, arguments);
    const url = typeof resource === 'string' ? resource : resource?.url || '';
    if (!SyncManager.isSyncing && url.includes('/cart/change')) {
      cartItemsEl?.setAttribute('aria-busy', 'true');
      // 50ms: response is already received so backend is updated; small delay
      // lets Dawn finish its own synchronous DOM work before we re-render.
      setTimeout(() => SyncManager.sync(cartItemsEl), 50);
    }
    return response;
  };

  // Initialise product page UI (only present on product pages).
  const containers = document.querySelectorAll('.pepbox-personalisation');
  containers.forEach((container) => {
    const uiRef = buildUI(container);
    const uploadRef = buildUpload(container, uiRef);
    const { attach } = buildCartManager(container, uiRef, uploadRef);
    attach();

    // Keep variant weight in sync so brand tile filtering stays accurate on variant switches.
    if (typeof subscribe === 'function') {
      subscribe('variant-change', (event) => {
        if (event?.data?.sectionId !== container.dataset.sectionId) return;
        const variant = event?.data?.variant;
        if (variant?.weight != null) uiRef.setVariantWeight(variant.weight);
      });
    }
  });

  document.querySelectorAll('.pepbox-order-summary').forEach(buildOrderSummary);
});
